import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    CloseButton,
    Heading,
    HStack,
    Input,
    Spacer,
    Stack,
    Table,
    Text,
    Textarea,
} from '@chakra-ui/react';
import { DownloadIcon, EditIcon, PlusIcon, RefreshIcon, TrashIcon, CheckCircleIcon } from '../components/Icons';
import { api, ExtractVersionResponse, SoftwareVersion, SoftwareVersionRequest, SoftwareVersionBundle, VersionExtractor } from '../api/api';
import { FormControl, FormErrorMessage, FormHelperText, FormLabel } from '../components/FormControls';
import { useConfirmation } from '../context/NotificationContext';
import SimpleModal from '../components/SimpleModal';

export default function SoftwareVersionsPage() {
    const [bundle, setBundle] = useState<SoftwareVersionBundle | null>(null);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingVersion, setEditingVersion] = useState<SoftwareVersion | null>(null);
    const [formData, setFormData] = useState<SoftwareVersionRequest>({
        fileName: '',
        versionNumber: '',
        downloadPath: '',
        description: '',
        fileSizeBytes: undefined,
        checksum: '',
        releaseType: ''
    });
    const [extractedInfo, setExtractedInfo] = useState<ExtractVersionResponse | null>(null);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [modalError, setModalError] = useState<string>('');
    const [fileNameError, setFileNameError] = useState<string>('');
    const [downloadPathError, setDownloadPathError] = useState<string>('');
    const [driveResolveLoading, setDriveResolveLoading] = useState(false);
    const [driveDownloadLoading, setDriveDownloadLoading] = useState(false);
    const [clearDownloadsLoading, setClearDownloadsLoading] = useState(false);
    const { confirm } = useConfirmation();

    // Manual load function - called only when user clicks refresh
    const loadSoftwareVersions = () => {
        setLoading(true);
        setError('');
        api.softwareVersions.grouped()
            .then(data => {
                setBundle(data);
            })
            .catch(err => {
                setError('Failed to load software versions: ' + (err as Error).message);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Load data on component mount
    useEffect(() => {
        loadSoftwareVersions();
    }, []);

    const handleFileNameChange = (fileName: string) => {
        // Update form data with filename
        const updatedFormData = { ...formData, fileName };
        
        // Validate file extension
        const lowerFileName = fileName.toLowerCase().trim();
        if (lowerFileName && !lowerFileName.endsWith('.zip')) {
            setFileNameError('File must be .zip');
        } else {
            setFileNameError('');
        }
        
        if (fileName.trim()) {
            try {
                // Extract version info using frontend utility
                const extracted = VersionExtractor.extractVersionInfo(fileName);
                setExtractedInfo({
                    version: extracted.version,
                    downloadPath: updatedFormData.downloadPath
                });
                
                // Update form data with extracted info
                updatedFormData.versionNumber = extracted.version;
            } catch (err) {
                setExtractedInfo(null);
                // Clear extracted fields on error
                updatedFormData.versionNumber = '';
            }
        } else {
            setExtractedInfo(null);
            // Clear all fields when filename is empty
            updatedFormData.versionNumber = '';
        }
        
        setFormData(updatedFormData);
    };

    const handleResolveDriveLink = async () => {
        const url = formData.downloadPath?.trim();
        setModalError('');

        if (!url) {
            setDownloadPathError('Download link is required');
            return;
        }

        setDownloadPathError('');
        setDriveResolveLoading(true);

        try {
            const res = await api.googleDrive.resolve(url);
            if (!res.exists) {
                setModalError(res.failureReason || 'File not found or link is not accessible');
                return;
            }

            if (res.fileName) {
                handleFileNameChange(res.fileName);
            } else {
                setModalError('Could not extract filename from the link. Try another public share link.');
            }

            if (res.fileSizeBytes != null) {
                setFormData(prev => ({ ...prev, fileSizeBytes: res.fileSizeBytes }));
            }

            if (res.isZip === false) {
                setFileNameError('File must be .zip');
            }
        } catch (err: any) {
            const apiError = err?.response?.data?.error;
            setModalError('Failed to fetch Drive metadata: ' + (apiError || err?.message || 'Unknown error'));
        } finally {
            setDriveResolveLoading(false);
        }
    };

    const handleDownloadToServer = async () => {
        const url = formData.downloadPath?.trim();
        setModalError('');

        if (!url) {
            setDownloadPathError('Download link is required');
            return;
        }

        setDownloadPathError('');
        setDriveDownloadLoading(true);

        try {
            const res = await api.googleDrive.downloadToServer(url, false);
            setSuccess(`Downloaded on server: ${res.fileName} (${formatFileSize(res.fileSizeBytes)})`);
            if (res.fileName) {
                handleFileNameChange(res.fileName);
            }
            if (res.fileSizeBytes != null) {
                setFormData(prev => ({ ...prev, fileSizeBytes: res.fileSizeBytes }));
            }
        } catch (err: any) {
            const apiError = err?.response?.data?.error;
            setModalError('Failed to download on server: ' + (apiError || err?.message || 'Unknown error'));
        } finally {
            setDriveDownloadLoading(false);
        }
    };

    const handleClearServerDownloads = async () => {
        setError('');
        setSuccess('');
        setClearDownloadsLoading(true);

        try {
            const res = await api.googleDrive.clearDownloads();
            setSuccess(`Cleared server downloads: ${res.deletedFiles} files (${formatFileSize(res.deletedBytes)})`);
        } catch (err: any) {
            const apiError = err?.response?.data?.error;
            setError('Failed to clear downloads: ' + (apiError || err?.message || 'Unknown error'));
        } finally {
            setClearDownloadsLoading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        
        const operation = editingVersion
            ? api.softwareVersions.update(editingVersion.id, formData)
            : api.softwareVersions.create(formData);
        
        operation
            .then(() => {
                setShowAddForm(false);
                setEditingVersion(null);
                setFormData({ 
                    fileName: '', 
                    versionNumber: '',
                    downloadPath: '',
                    description: '', 
                    fileSizeBytes: undefined, 
                    checksum: '',
                    releaseType: ''
                });
                setExtractedInfo(null);
                setSuccess(editingVersion ? 'Software version updated successfully' : 'Software version added successfully');
                // Manual refresh after adding/updating
                loadSoftwareVersions();
            })
            .catch(err => {
                setError('Failed to save software version: ' + (err as Error).message);
            });
    };

    const handleEdit = (version: SoftwareVersion) => {
        setEditingVersion(version);
        setFormData({
            fileName: version.fileName,
            versionNumber: version.versionNumber,
            downloadPath: version.downloadPath,
            description: version.description || '',
            fileSizeBytes: version.fileSizeBytes,
            checksum: version.checksum || '',
            releaseType: version.releaseType || 'Production'
        });
        // Show extracted info for editing
        setExtractedInfo({
            version: version.versionNumber,
            downloadPath: version.downloadPath
        });
        setShowAddForm(true);
    };

    const handleDelete = (id: number, versionNumber: string) => {
        confirm({
            title: 'Delete Software Version',
            message: `Are you sure you want to delete v${versionNumber}? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmColorScheme: 'red',
            onConfirm: () => {
                setError('');
                setSuccess('');
                api.softwareVersions.delete(id)
                    .then(() => {
                        setSuccess('Software version deleted successfully');
                        // Manual refresh after deleting
                        loadSoftwareVersions();
                    })
                    .catch(err => {
                        setError('Failed to delete software version: ' + (err as Error).message);
                    });
            }
        });
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return '';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <Stack gap={6}>
            <Heading size="lg">Software Version Management</Heading>
            <HStack gap={2}>
                <Button
                    variant="outline"
                    onClick={loadSoftwareVersions}
                    loading={loading}
                    display="inline-flex"
                    alignItems="center"
                    gap={2}
                >
                    <RefreshIcon />
                    Refresh
                </Button>
                <Button
                    colorPalette="brand"
                    onClick={() => {
                        setShowAddForm(true);
                        setEditingVersion(null);
                        setFormData({
                            fileName: '',
                            versionNumber: '',
                            downloadPath: '',
                            description: '',
                            fileSizeBytes: undefined,
                            checksum: '',
                            releaseType: ''
                        });
                        setExtractedInfo(null);
                    }}
                    display="inline-flex"
                    alignItems="center"
                    gap={2}
                >
                    <PlusIcon />
                    Add New Version
                </Button>
                <Spacer />
                <Button
                    variant="outline"
                    display="inline-flex"
                    alignItems="center"
                    gap={2}
                    onClick={() => window.open('https://drive.google.com/drive/folders/1qdLULmdUhP_WxE_NoQ74BGzEAg1lJkpx', '_blank', 'noopener,noreferrer')}
                >
                    <img src="/DriveIcon.jpeg" alt="Drive" style={{ width: '16px', height: '16px' }} />
                    Drive Backup Folder
                </Button>
            </HStack>

            {error && (
                <Alert.Root status="error" borderRadius="md">
                    <Alert.Content flex="1">
                        <Alert.Description>{error}</Alert.Description>
                    </Alert.Content>
                    <CloseButton position="relative" onClick={() => setError('')} />
                </Alert.Root>
            )}

            {success && (
                <Alert.Root status="success" borderRadius="md">
                    <Alert.Content flex="1">
                        <Alert.Description>{success}</Alert.Description>
                    </Alert.Content>
                    <CloseButton position="relative" onClick={() => setSuccess('')} />
                </Alert.Root>
            )}

            <SimpleModal
                isOpen={showAddForm}
                onClose={() => {
                    setShowAddForm(false);
                    setEditingVersion(null);
                    setExtractedInfo(null);
                    setModalError('');
                }}
                title={editingVersion ? 'Edit Software Version' : 'Add New Software Version'}
                size="md"
                footer={
                    <HStack gap={3} justify="flex-end">
                        <Button variant="ghost" onClick={() => {
                            setShowAddForm(false);
                            setEditingVersion(null);
                            setExtractedInfo(null);
                        }}>
                            Cancel
                        </Button>
                        <Button
                            colorPalette="brand"
                            disabled={!formData.downloadPath.trim() || !formData.fileName.trim() || !formData.releaseType || !!fileNameError || !!downloadPathError}
                            onClick={(e: any) => handleSubmit(e)}
                        >
                            {editingVersion ? 'Update Version' : 'Add Version'}
                        </Button>
                    </HStack>
                }
            >
                <Stack gap={4}>
                    {modalError && (
                        <Alert.Root status="error" borderRadius="md">
                            <Alert.Content flex="1">
                                <Alert.Description>{modalError}</Alert.Description>
                            </Alert.Content>
                            <CloseButton position="relative" onClick={() => setModalError('')} />
                        </Alert.Root>
                    )}
                    <FormControl isRequired isInvalid={!!downloadPathError}>
                        <FormLabel>Download Link</FormLabel>
                        <HStack gap={2} align="stretch" w="full">
                            <Input
                                flex="1"
                                minW={0}
                                value={formData.downloadPath}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const v = e.target.value;
                                    setFormData({ ...formData, downloadPath: v });
                                    if (downloadPathError && v.trim()) setDownloadPathError('');
                                }}
                                placeholder="https://drive.google.com/file/d/.../view"
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                loading={driveResolveLoading}
                                onClick={handleResolveDriveLink}
                                whiteSpace="nowrap"
                                flexShrink={0}
                            >
                                Fetch
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                loading={driveDownloadLoading}
                                onClick={handleDownloadToServer}
                                whiteSpace="nowrap"
                                flexShrink={0}
                            >
                                Download
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                colorPalette="red"
                                loading={clearDownloadsLoading}
                                onClick={() =>
                                    confirm({
                                        title: 'Clear Server Downloads',
                                        message: 'Delete all downloaded files from the server Downloads folder?',
                                        confirmText: 'Clear',
                                        cancelText: 'Cancel',
                                        confirmColorScheme: 'red',
                                        onConfirm: handleClearServerDownloads
                                    })
                                }
                                whiteSpace="nowrap"
                                flexShrink={0}
                            >
                                Clear
                            </Button>
                        </HStack>
                        {downloadPathError ? (
                            <FormErrorMessage>{downloadPathError}</FormErrorMessage>
                        ) : (
                            <FormHelperText>Paste a public Google Drive link and click Fetch to auto-fill filename.</FormHelperText>
                        )}
                    </FormControl>

                    <FormControl isRequired isInvalid={!!fileNameError}>
                        <FormLabel>File Name</FormLabel>
                        <Input
                            value={formData.fileName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileNameChange(e.target.value)}
                            placeholder="flower 1.2.3.zip"
                        />
                        {fileNameError ? (
                            <FormErrorMessage>{fileNameError}</FormErrorMessage>
                        ) : (
                            <FormHelperText>Version number is auto-extracted from the filename.</FormHelperText>
                        )}
                    </FormControl>

                    {extractedInfo && (
                        <Alert.Root status="info" borderRadius="md">
                            <Alert.Content>
                                <Stack gap={1} fontSize="sm">
                                    <Text fontWeight="bold">Auto-extracted Information</Text>
                                    <Text>Version: {extractedInfo.version}</Text>
                                    <Text>Link: {extractedInfo.downloadPath}</Text>
                                </Stack>
                            </Alert.Content>
                        </Alert.Root>
                    )}

                    <FormControl>
                        <FormLabel>Description</FormLabel>
                        <Textarea
                            rows={3}
                            value={formData.description}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                            placeholder="Optional description"
                        />
                    </FormControl>

                    <FormControl isRequired>
                        <FormLabel>Release Type</FormLabel>
                        <HStack gap={3}>
                            {(['Production', 'Test'] as const).map((type) => {
                                const selected = formData.releaseType === type;
                                return (
                                    <Button
                                        key={type}
                                        variant={selected ? 'solid' : 'outline'}
                                        colorPalette={selected ? 'brand' : 'gray'}
                                        onClick={() => setFormData({ ...formData, releaseType: type })}
                                        display="inline-flex"
                                        alignItems="center"
                                        gap={2}
                                        px={5}
                                    >
                                        {selected && <CheckCircleIcon />}
                                        {type}
                                    </Button>
                                );
                            })}
                        </HStack>
                    </FormControl>
                </Stack>
            </SimpleModal>

            {(!bundle || bundle.versions.length === 0) ? (
                <Card.Root>
                    <Card.Body p={{ base: 2, md: 3 }}>
                        <Text color="gray.500">
                            No software versions found. Click "Add New Version" to get started.
                        </Text>
                        <Text fontSize="sm" color="gray.500" mt={2}>
                            Example: enter "flower 1.2.3.zip" to auto-detect version.
                        </Text>
                    </Card.Body>
                </Card.Root>
            ) : (
                <Card.Root variant="outline">
                    <Card.Header p={{ base: 2, md: 3 }}>
                        <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
                            <Heading size="md">All Versions</Heading>
                            <HStack gap={2}>
                                {bundle.latestVersion && (
                                    <Badge colorPalette="brand">Latest v{bundle.latestVersion.versionNumber}</Badge>
                                )}
                                <Badge colorPalette="gray">
                                    {bundle.versions.length} version{bundle.versions.length !== 1 ? 's' : ''}
                                </Badge>
                            </HStack>
                        </HStack>
                    </Card.Header>
                    <Card.Body p={{ base: 1, md: 2 }} overflowX="auto">
                        <Table.Root>
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeader whiteSpace="nowrap">Version</Table.ColumnHeader>
                                    <Table.ColumnHeader whiteSpace="nowrap">File Name</Table.ColumnHeader>
                                    <Table.ColumnHeader whiteSpace="nowrap">Type</Table.ColumnHeader>
                                    <Table.ColumnHeader whiteSpace="nowrap">Description</Table.ColumnHeader>
                                    <Table.ColumnHeader whiteSpace="nowrap">Size</Table.ColumnHeader>
                                    <Table.ColumnHeader whiteSpace="nowrap">Date Added</Table.ColumnHeader>
                                    <Table.ColumnHeader textAlign="right" whiteSpace="nowrap">Actions</Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {bundle.versions.map((version, idx) => (
                                    <Table.Row key={version.id}>
                                        <Table.Cell>
                                            <Badge colorPalette={idx === 0 ? 'brand' : 'gray'}>
                                                v{version.versionNumber}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>{version.fileName}</Table.Cell>
                                        <Table.Cell>
                                            <Badge colorPalette={version.releaseType === 'Test' ? 'orange' : 'green'}>
                                                {version.releaseType || 'Production'}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell maxW="300px">
                                            <Text lineClamp={2}>{version.description || '—'}</Text>
                                        </Table.Cell>
                                        <Table.Cell>{formatFileSize(version.fileSizeBytes)}</Table.Cell>
                                        <Table.Cell>{formatDate(version.dateAdded)}</Table.Cell>
                                        <Table.Cell textAlign="right">
                                            <HStack gap={1} justify="flex-end">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => window.open(version.downloadPath, '_blank')}
                                                    title="Download"
                                                >
                                                    <DownloadIcon />
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => handleEdit(version)} title="Edit">
                                                    <EditIcon />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    colorPalette="red"
                                                    onClick={() => handleDelete(version.id, version.versionNumber)}
                                                    title="Delete"
                                                >
                                                    <TrashIcon />
                                                </Button>
                                            </HStack>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </Card.Body>
                </Card.Root>
            )}
        </Stack>
    );
}
